import { useEffect, useState } from 'react';
import Lenis from 'lenis';
import { MotionConfig, useReducedMotion } from 'framer-motion';
import { toast } from 'sonner';
import { Header } from './components/pitch/Header';
import { Hero } from './components/pitch/Hero';
import { ExecutiveSummary } from './components/pitch/ExecutiveSummary';
import { Thesis, BusinessModel } from './components/pitch/Story';
import { Products } from './components/pitch/Products';
import { Financials } from './components/pitch/Financials';
import { Expansion } from './components/pitch/Expansion';
import { Materials } from './components/pitch/Materials';
import { VideoDialog, SaveDialog } from './components/pitch/Dialogs';
import { Presentation, PrintDeck } from './components/pitch/Deck';
import { CompareDialog } from './components/pitch/CompareDialog';
import { useProject } from './hooks/useProject';
import { PlanEditor } from './components/pitch/PlanEditor';
import { PlanAppendix } from './components/pitch/PlanAppendix';
import { requestView } from './lib/viewBridge';
import { stickyOffset } from './lib/scrollWithOffset';
import { FileText } from 'lucide-react';
import './App.css';
import './editor.css';
import './sales-events.css';
import './print.css';
import './monthly-sales.css';
import './gallery.css';
import './executive.css';
import './b2b-timeline.css';
import './sober.css';

export default function PitchApp() {
  const project = useProject();
  const [modal, setModal] = useState('');
  const [active, setActive] = useState('inicio');
  const [editorTab, setEditorTab] = useState('products');
  const reduced = useReducedMotion();

  useEffect(() => {
    let lenis = null;
    if (!reduced) {
      lenis = new Lenis({ autoRaf: true, anchors: false, duration: 1.15, smoothWheel: true });
    }
    // Handle in-page anchor links ourselves so the target lands just below the
    // sticky header stack (mode-selector bar + site header), measured live so it
    // is correct under body zoom and at any viewport.
    const onAnchorClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href === '#') return;
      const id = decodeURIComponent(href.slice(1));
      const el = document.getElementById(id);
      if (!el) return;
      e.preventDefault();
      // Compute an absolute scroll position from live geometry so it stays
      // correct under body zoom. We pass a NUMBER to lenis.scrollTo (not the
      // element) because Lenis's element measurement is not zoom-aware.
      const off = stickyOffset(['.site-header', '.mode-selector-bar'], 12);
      const y = Math.max(0, el.getBoundingClientRect().top + window.scrollY - off);
      if (lenis) {
        lenis.scrollTo(y);
      } else {
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
      if (window.history.replaceState) window.history.replaceState(null, '', href);
    };
    document.addEventListener('click', onAnchorClick);
    return () => {
      document.removeEventListener('click', onAnchorClick);
      if (lenis) lenis.destroy();
    };
  }, [reduced]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: '-15% 0px -60% 0px' },
    );
    document.querySelectorAll('main section[id]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const close = (open) => !open && setModal('');
  const sources = () => setModal('materials');

  const present = () => {
    if (!project.result || project.busy || project.error) {
      toast.info('Aguarde pelos cálculos ou corrija os pressupostos antes de apresentar.');
      return;
    }
    setModal('presentation');
  };

  const print = (mode = 'full') => {
    if (!project.result || project.busy || project.error) {
      toast.info('Aguarde pelos cálculos ou corrija os pressupostos antes de exportar.');
      return;
    }
    document.body.dataset.printMode = typeof mode === 'string' ? mode : 'full';
    window.print();
  };

  const doEdit = (tab = 'products') => {
    if (!project.plan) {
      toast.info('Aguarde pelo carregamento do plano.');
      return;
    }
    setEditorTab(tab);
    setModal('editor');
  };

  const edit = (tab = 'products') => doEdit(tab);

  const save = () => setModal('save');

  const openAsDossier = () => {
    // Best-effort mapping between the pitch plan and one of the two dossier
    // scenarios: pick the scenario whose Y1 total (LioPT-5 + LioPT-9) is
    // closest to the sum of LioPT-5 + LioPT-9 units in the pitch's monthly
    // forecast. Other pitch products are informational and don't feed the
    // acquisition engine, which only prices those two models.
    const products = project.plan?.products || [];
    const y1SmallMedium = products
      .filter((p) => p.id === 'liopt-5' || p.id === 'liopt-9')
      .reduce((sum, p) => sum + (p.units || 0), 0);
    const scenarioKey = y1SmallMedium >= 42 ? 'optimistic_moderate' : 'conservative';
    const label = `${y1SmallMedium} unidades LioPT-5 + LioPT-9 no ano 1 → cenário ${scenarioKey === 'conservative' ? 'Conservador' : 'Otimista moderado'}.`;
    requestView('dossier', { scenarioKey, label, sourceUnits: y1SmallMedium });
    toast.success('A abrir planeamento com o cenário pré-selecionado.');
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="site-shell">
        <Header onPresent={present} onSources={sources} onEdit={() => edit()} active={active} />
        {project.error && (
          <div className="project-error" role="alert" data-testid="project-error-banner">
            <span>{project.error}</span>
            <button data-testid="retry-project-button" onClick={project.retry}>Tentar novamente</button>
          </div>
        )}
        <main>
          <Hero onPresent={present} onVideo={() => setModal('video')} />
          <ExecutiveSummary result={project.result} plan={project.plan} busy={project.busy} error={project.error} isExample={project.isExample} onEdit={edit} scenarios={project.scenarios} activeScenario={project.activeScenario} onScenarioChange={project.setActiveScenario} />
          <Thesis narrative={project.plan?.narrative} />
          <Products products={project.products} vat={project.plan?.vat ?? 23} onSources={sources} />
          <BusinessModel narrative={project.plan?.narrative} plan={project.plan} result={project.result} onEdit={edit} />
          <Financials {...project} onSave={save} onCompare={() => setModal('compare')} onEdit={edit} onSources={sources} />
          <Expansion {...project} onPrint={() => print()} onSources={sources} onEdit={edit} />
        </main>
      </div>
      <Materials open={modal === 'materials'} onOpenChange={close} onVideo={() => setModal('video')} />
      <VideoDialog open={modal === 'video'} onOpenChange={close} />
      <SaveDialog open={modal === 'save'} onOpenChange={close} scenario={project.scenario} plan={project.plan} onSaved={project.replacePlan} />
      <PlanEditor open={modal === 'editor'} onOpenChange={close} initialTab={editorTab} {...project} onSave={save} onCompare={() => setModal('compare')} onPrint={() => print()} />
      <CompareDialog open={modal === 'compare'} onOpenChange={close} plan={project.plan} result={project.result} onSave={save} />
      <Presentation open={modal === 'presentation'} onOpenChange={close} {...project} onPrint={() => print('pitch')} />
      <PrintDeck {...project} />
      <PlanAppendix {...project} />
      <button
        type="button"
        onClick={openAsDossier}
        className="pitch-to-dossier"
        data-testid="pitch-to-dossier"
        aria-label="Ver este plano no modo planeamento executivo"
      >
        <FileText size={14} />
        Ver planeamento executivo
      </button>
    </MotionConfig>
  );
}
